import { format as formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Euro, Info, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";
import type { ClaimExpenseInput } from "@upclaim/sdk/src/data-contracts";

import { Can } from "@/components/can";
import { FileCard } from "@/components/files/file-card";
import { FileDropzone } from "@/components/files/file-dropzone";
import { fileToPayload } from "@/components/files/use-file-to-payload";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import type { ClaimWithRelations } from "../api/use-claim";
import { useAddClaimExpense, useDeleteExpense, useUpdateExpense } from "../api/use-expense-mutations";
import {
  type ExpenseMonetaryDemandType,
  type ExpenseType,
  expenseTypesMap,
  getExpenseMonetaryDemandType,
} from "../lib/expense-types";
import { CurrencyCombobox } from "./currency-combobox";
import type { ClaimExpenseItem } from "./expenses-list";

const MONETARY_DEMAND_OPTIONS: Array<{ value: ExpenseMonetaryDemandType; label: string }> = [
  { value: "EC261_ASSISTANCE_DUTY", label: "Devoir d'assistance (261/2004)" },
  { value: "MC99_FINANCIAL_DAMAGES", label: "Convention de Montréal (MC99)" },
];

const LOST_PRESTATION_OPTIONS = [
  { value: true, label: "Prestation perdue" },
  { value: false, label: "Nouvelle dépense" },
];

function toExpenseMonetaryDemandType(type: string | null | undefined): ExpenseMonetaryDemandType {
  return type === "MC99_FINANCIAL_DAMAGES" ? "MC99_FINANCIAL_DAMAGES" : "EC261_ASSISTANCE_DUTY";
}

interface UpsertExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claim: ClaimWithRelations;
  existingExpense?: ClaimExpenseItem | null;
}

/** Port of expenses/UpsertExpense.vue (create/edit/delete expense form). */
export function UpsertExpenseDialog({
  open,
  onOpenChange,
  claim,
  existingExpense,
}: UpsertExpenseDialogProps) {
  const isB2B = Boolean(claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany);

  const addExpense = useAddClaimExpense(claim.id);
  const updateExpense = useUpdateExpense(claim.id);
  const deleteExpense = useDeleteExpense(claim.id);
  const isLoading = addExpense.isPending || updateExpense.isPending || deleteExpense.isPending;

  const [expenseName, setExpenseName] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [expenseType, setExpenseType] = useState<ExpenseType>("RETURN_FLIGHT");
  const [isLostPrestation, setIsLostPrestation] = useState(false);
  const [monetaryDemandType, setMonetaryDemandType] =
    useState<ExpenseMonetaryDemandType>("EC261_ASSISTANCE_DUTY");
  const [paidBy, setPaidBy] = useState("TRAVEL_AGENCY");
  const [file, setFile] = useState<File | null>(null);

  // Re-seed the form each time the dialog opens (mirrors Vue's defaultExpense).
  useEffect(() => {
    if (!open) {
      return;
    }
    const expense = existingExpense?.expense;
    setExpenseName(expense?.expenseName ?? "");
    setPriceInput(expense ? (Number(expense.price) / 100).toFixed(2) : "");
    setCurrency(expense?.currencyCode ?? "EUR");
    setOccurredAt(expense?.occurredAt ? new Date(expense.occurredAt) : undefined);
    setExpenseType(expense?.expenseType.type ?? "RETURN_FLIGHT");
    setIsLostPrestation(expense?.isLostPrestation ?? false);
    setMonetaryDemandType(
      toExpenseMonetaryDemandType(expense?.claimMonetaryDemand?.monetaryDemandType?.type),
    );
    // Passenger expenses carry the passenger's name in paidBy (retrieveClaimWithAllRelations), the option value is the userClaimId
    setPaidBy(existingExpense?.userClaimId ?? existingExpense?.paidBy ?? "TRAVEL_AGENCY");
    setFile(null);
  }, [open, existingExpense]);

  /** Mirrors updateComputedMonetaryDemandType (Vue). */
  function recomputeMonetaryDemandType(nextIsLost: boolean, nextType: ExpenseType) {
    setMonetaryDemandType(getExpenseMonetaryDemandType(nextIsLost, nextType).type);
  }

  const paidByOptions: Array<{ value: string; label: string; disabled?: boolean }> = [
    { value: "TRAVEL_AGENCY", label: "Perte agence" },
    { value: "BUSINESS_TRAVEL_COMPANY", label: "Facturé sur le compte de l'entreprise", disabled: !isB2B },
    ...claim.usersClaims.map((userClaim) => ({
      value: userClaim.id,
      label: `Payé par ${userClaim.user.firstName} ${userClaim.user.lastName?.toUpperCase() ?? ""} (Passager)`,
    })),
  ];
  const selectedPaidByLabel = paidByOptions.find((option) => option.value === paidBy)?.label ?? paidBy;

  async function buildPayload(): Promise<ClaimExpenseInput> {
    const parsed = Number.parseFloat(priceInput.replace(",", "."));
    const cents = Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);

    const payload: ClaimExpenseInput = {
      paidBy,
      monetaryDemandType,
      expense: {
        // UTC midnight of the picked day, so the day doesn't shift when serialized
        occurredAt: occurredAt
          ? new Date(Date.UTC(occurredAt.getFullYear(), occurredAt.getMonth(), occurredAt.getDate()))
          : undefined,
        expenseType,
        price: {
          amount: cents,
          currency,
          precision: 2,
        },
        isLostPrestation,
        expenseName,
      },
    };
    if (file) {
      const { filename, dataUrl, mimeType } = await fileToPayload(file);
      payload.expense.file = { name: filename, document: dataUrl, mimeType };
    }
    return payload;
  }

  async function submit() {
    try {
      const payload = await buildPayload();
      if (existingExpense) {
        const { success } = await updateExpense.mutateAsync({
          expenseId: existingExpense.expenseId,
          payload,
        });
        if (success) {
          onOpenChange(false);
        }
        return;
      }
      const { success } = await addExpense.mutateAsync(payload);
      if (success) {
        onOpenChange(false);
      }
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  async function removeExpense() {
    if (!existingExpense) {
      return;
    }
    try {
      const { success } = await deleteExpense.mutateAsync(existingExpense.expenseId);
      if (success) {
        onOpenChange(false);
      }
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existingExpense ? "Modifier la dépense" : "Ajouter une dépense"}</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          {priceInput ? (
            <Alert variant="info" className="mb-4 text-left">
              <Info aria-hidden="true" />
              <AlertTitle>Cette dépense est comptabilisée sous :</AlertTitle>
              <AlertDescription>
                {monetaryDemandType === "EC261_ASSISTANCE_DUTY" ? (
                  <ul className="list-disc space-y-1 pl-5">
                    <li>Le « Devoir d'assistance »</li>
                    <li>Elle a toutes les chances d'être remboursée par la compagnie</li>
                  </ul>
                ) : (
                  <ul className="list-disc space-y-1 pl-5">
                    <li>La « Convention de Montréal de 1999 »</li>
                    <li>Cela est est souvent plus complexe à obtenir réparation</li>
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          ) : null}

          <form className="text-left" onSubmit={(event) => event.preventDefault()}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expense-name" className="block text-sm font-medium text-gray-900">
                  Nom de la dépense
                </Label>
                <Input
                  id="expense-name"
                  className="mt-2"
                  placeholder="Avion retour 26/05"
                  value={expenseName}
                  onChange={(event) => setExpenseName(event.currentTarget.value)}
                />
              </div>
              <div>
                <Label htmlFor="expense-price" className="block text-sm font-medium text-gray-900">
                  Montant
                </Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    id="expense-price"
                    className="flex-1"
                    inputMode="decimal"
                    placeholder="128.52"
                    value={priceInput}
                    onChange={(event) => setPriceInput(event.currentTarget.value)}
                  />
                  <CurrencyCombobox className="w-28" value={currency} onChange={setCurrency} />
                </div>
              </div>
              <div className="col-span-2">
                <Label htmlFor="expense-occurred-at" className="block text-sm font-medium text-gray-900">
                  Date de la dépense
                </Label>
                <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                  <PopoverTrigger
                    render={
                      <Button
                        id="expense-occurred-at"
                        variant="outline"
                        className="mt-2 w-full justify-start font-normal"
                      />
                    }
                  >
                    <CalendarIcon data-icon="inline-start" />
                    {occurredAt
                      ? formatDate(occurredAt, "d MMMM yyyy", { locale: fr })
                      : "Sélectionner une date"}
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={fr}
                      selected={occurredAt}
                      defaultMonth={occurredAt}
                      disabled={{ after: new Date() }}
                      onSelect={(date) => {
                        setOccurredAt(date);
                        setIsDatePickerOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-900">Type de dépense</Label>
                <Select
                  value={expenseType}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      const nextType = value as ExpenseType;
                      setExpenseType(nextType);
                      recomputeMonetaryDemandType(isLostPrestation, nextType);
                    }
                  }}
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue>{expenseTypesMap[expenseType].label}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(expenseTypesMap).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-900">Perte ou nouvelle dépense ?</Label>
                <Select
                  value={String(isLostPrestation)}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      const nextIsLost = value === "true";
                      setIsLostPrestation(nextIsLost);
                      recomputeMonetaryDemandType(nextIsLost, expenseType);
                    }
                  }}
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue>
                      {LOST_PRESTATION_OPTIONS.find((option) => option.value === isLostPrestation)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {LOST_PRESTATION_OPTIONS.map((option) => (
                      <SelectItem key={String(option.value)} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
              <div className="my-6">
                <Label className="block text-sm font-medium text-gray-900">Base règlementaire</Label>
                <p className="text-xs text-slate-600">
                  Devoir d'assistance (261/2004) ou Convention de Montréal (MC99)
                </p>
                <Select
                  value={monetaryDemandType}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      setMonetaryDemandType(value as ExpenseMonetaryDemandType);
                    }
                  }}
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue>
                      {MONETARY_DEMAND_OPTIONS.find((option) => option.value === monetaryDemandType)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {MONETARY_DEMAND_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Can>

            <div className="my-6">
              <Label className="block text-sm font-medium text-gray-900">Qui a payé ?</Label>
              <p className="text-xs text-slate-600">Nous ferons indemniser le payeur</p>
              <Select
                value={paidBy}
                onValueChange={(value) => {
                  if (typeof value === "string") {
                    setPaidBy(value);
                  }
                }}
              >
                <SelectTrigger className="mt-2 w-full">
                  <SelectValue>{selectedPaidByLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {paidByOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="mt-4">
                <p className="text-sm font-medium leading-6 text-gray-900">Facture</p>
                <p className="text-sm text-slate-600">Merci d'indiquer 1 justificatif par dépense</p>
                <div className="mt-2">
                  {file ? (
                    <FileCard
                      name={file.name}
                      size={file.size}
                      mimeType={file.type}
                      onRemove={() => setFile(null)}
                    />
                  ) : (
                    <FileDropzone
                      onFiles={(files) => setFile(files[0] ?? null)}
                      hint="Priviligier les PDF · Jusqu'à 350 MB."
                      maxSize={350 * 1024 * 1024}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-row-reverse items-center gap-6">
              <Button type="button" disabled={isLoading} onClick={() => void submit()}>
                <Euro data-icon="inline-start" />
                Enregistrer la dépense
              </Button>
              {existingExpense ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => void removeExpense()}
                >
                  <Trash2 data-icon="inline-start" />
                  Supprimer la dépense
                </Button>
              ) : null}
            </div>
          </form>
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
